import React, { useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { Trash2, Loader2, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";

export default function DeleteCompanyButton({ companyId, companyName, subscriptionStatus, userCount, onDeleted }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [code, setCode] = useState("");
    const [phrase, setPhrase] = useState("");
    const { call: requestCode, isLoading: requesting } = useBluelagoon({ method: "requestCompanyDeletion" });
    const { call: callDelete, isLoading: deleting } = useBluelagoon({ method: "deleteCompany" });
    const isActive = subscriptionStatus === "ACTIVE";
    const expectedPhrase = `DELETE ${companyName}`;

    const handleClose = () => { setOpen(false); setStep(1); setCode(""); setPhrase(""); };

    const handleRequestCode = useCallback(async () => {
        try {
            const res = await requestCode({ companyId });
            if (res?.data?.email) { toast.success(`Code sent to ${res.data.email}`); setStep(2); }
        } catch (err) { toast.error(err?.error || "Failed to send code"); }
    }, [requestCode, companyId]);

    const handleDelete = useCallback(async () => {
        try {
            const res = await callDelete({ companyId, verificationCode: code, confirmationPhrase: phrase });
            if (res?.data) { toast.success(`${companyName} deleted.`); handleClose(); onDeleted?.(); }
        } catch (err) { toast.error(err?.error || "Failed to delete company"); }
    }, [callDelete, companyId, code, phrase, companyName, onDeleted]);

    return (
        <>
            <Button variant="destructive" size="sm" onClick={() => setOpen(true)} className="gap-2">
                <Trash2 className="h-4 w-4" />Delete Company
            </Button>
            <AlertDialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />Delete Company
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p>Permanently delete <strong className="text-foreground">{companyName}</strong> and all data ({userCount || 0} users, apps, workflows, files).</p>
                                {isActive && <p className="text-destructive font-medium">Active subscription — cancel it first.</p>}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {!isActive && step === 1 && (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Mail className="h-4 w-4" />A verification code will be sent to your email.
                        </p>
                    )}
                    {!isActive && step === 2 && (
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label>Verification Code</Label>
                                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="6-digit code from email" maxLength={6} />
                            </div>
                            <div className="space-y-1">
                                <Label>Type <strong className="text-destructive">{expectedPhrase}</strong></Label>
                                <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={expectedPhrase} />
                            </div>
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={requesting || deleting} onClick={handleClose}>Cancel</AlertDialogCancel>
                        {!isActive && step === 1 && (
                            <Button variant="destructive" onClick={handleRequestCode} disabled={requesting}>
                                {requesting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send Code
                            </Button>
                        )}
                        {!isActive && step === 2 && (
                            <Button variant="destructive" onClick={handleDelete} disabled={phrase !== expectedPhrase || code.length < 6 || deleting}>
                                {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete Permanently
                            </Button>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
